/**
 * Rex Mortis — Privacy Policy
 *
 * Plain-English template covering what data we collect, why, and how
 * we handle it. Should be reviewed by a qualified lawyer for the
 * relevant jurisdictions (GDPR if EU users, CCPA if California, etc.)
 * before public launch.
 */
import { mountShellOrStandalone } from '../components/Shell.js';

export async function renderPrivacy(root: HTMLElement): Promise<void> {
  mountShellOrStandalone(root, (main) => {
    main.innerHTML = `
      <img class="page-banner" src="/img/banners/privacy.webp" alt="" onerror="this.remove()" />
      <h1>Privacy Policy</h1>
      <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
        What we collect, why we collect it, and what you can do about it.
      </p>

      <section class="panel">
        <h2 style="margin-top: 0;">1 · What we collect</h2>
        <p>When you use Rex Mortis, we collect:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li><strong>Account information:</strong> your email address and a securely hashed password.</li>
          <li><strong>Character data:</strong> your character name, in-game stats, inventory, history of actions, chat messages, and any other in-game state.</li>
          <li><strong>Technical data:</strong> IP address, browser type and version, device type, time of access, and pages visited. This is used for security, analytics, and to defeat multi-accounting.</li>
          <li><strong>Communications:</strong> any feedback or bug reports you submit, including your character name and the page URL at the time.</li>
        </ul>
        <p>We do <strong>not</strong> collect: your real name, postal address, phone number, payment information (we don't take payments), or any sensitive categories of personal data (health, sexual orientation, political views, religion, etc.).</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">2 · Why we collect it</h2>
        <p>We use the data we collect to:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li>Operate the Service — store your character, deliver gameplay actions, run leaderboards, deliver mail and chat.</li>
          <li>Send transactional emails — email verification, password resets, and (where applicable) bug-report receipts.</li>
          <li>Protect the Service — detect and block multi-accounting, brute-force login attempts, exploits, and abuse.</li>
          <li>Respond to feedback and support requests.</li>
          <li>Comply with legal obligations.</li>
        </ul>
        <p>We do not use your data for advertising. We do not sell your data to anyone.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">3 · How long we keep it</h2>
        <p>We retain your account and character data as long as your account is active. If you close your account, we delete it within 30 days, except where we're required by law to retain certain records (e.g. transaction logs for tax purposes — currently we hold none of these, since no payments are involved).</p>
        <p>Server access logs are retained for 90 days for security and debugging, then automatically deleted.</p>
        <p>Email verification and password reset tokens expire automatically (60 minutes for password reset, 24 hours for verification).</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">4 · Who we share it with</h2>
        <p>We share data only with service providers that help us run the Service:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li><strong>Resend</strong> — email delivery for verification and password resets. Resend receives your email address and the body of any transactional email we send. <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener">Resend's privacy policy</a>.</li>
          <li><strong>Cloudflare</strong> — DNS and (where applicable) CDN/edge for our domains. Cloudflare may process your IP address and request metadata. <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener">Cloudflare's privacy policy</a>.</li>
          <li><strong>Our hosting provider</strong> — for storing your account and character data in our database. Currently this is Neon (managed Postgres) and Fly.io (application hosting).</li>
        </ul>
        <p>We do not sell or rent your personal data. We will only disclose data to third parties if required by law (e.g. a valid court order) or to protect the security of the Service.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">5 · Cookies and local storage</h2>
        <p>We use the following:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li><strong>An httpOnly refresh-token cookie</strong> — keeps you signed in between visits. Essential for the Service.</li>
          <li><strong>localStorage</strong> — stores your theme preference (light/dark), tutorial progress display state, and similar UI preferences. Stays on your device.</li>
        </ul>
        <p>We do not use third-party tracking cookies, advertising cookies, or analytics cookies.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">6 · Your rights</h2>
        <p>Depending on where you live, you may have the following rights regarding your personal data:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li><strong>Access:</strong> request a copy of the data we hold about you.</li>
          <li><strong>Correction:</strong> ask us to fix data that's inaccurate.</li>
          <li><strong>Deletion:</strong> ask us to delete your account and personal data ("right to be forgotten").</li>
          <li><strong>Portability:</strong> receive your data in a machine-readable format.</li>
          <li><strong>Objection:</strong> object to certain uses of your data.</li>
        </ul>
        <p>To exercise any of these rights, send a request through the feedback form in Settings (category: Other) or by emailing <strong>feedback.playrexmortis@gmail.com</strong>. We'll respond within 30 days.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">7 · Security</h2>
        <p>We take reasonable measures to protect your data:</p>
        <ul style="margin: 0; padding-left: 1.2em;">
          <li>Passwords are hashed with bcrypt (cost 12) — we never store them in plain text.</li>
          <li>Refresh tokens are stored only as bcrypt hashes — a database leak cannot replay them.</li>
          <li>All traffic is encrypted with TLS in production.</li>
          <li>The Service is rate-limited to defeat brute-force and credential-stuffing attacks.</li>
          <li>We restrict database access to the application and a small number of administrators.</li>
        </ul>
        <p>No system is perfectly secure. If you suspect your account has been compromised, change your password immediately and let us know via the feedback form.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">8 · Children's privacy</h2>
        <p>The Service is not directed at children under 13. We do not knowingly collect personal data from children under 13. If you believe we have collected such data, please contact us so we can delete it.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">9 · International data transfers</h2>
        <p>The Service is hosted in [region — currently Sydney via Fly.io and Neon]. If you access the Service from outside this region, your data may be transferred to and processed in other countries. By using the Service, you consent to such transfers, subject to applicable data-protection laws.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">10 · Changes to this Policy</h2>
        <p>We may update this Privacy Policy from time to time. Material changes will be announced in the patch notes and, where appropriate, by email. The "Last updated" date below indicates when changes were last made.</p>
      </section>

      <section class="panel">
        <h2 style="margin-top: 0;">11 · Contact</h2>
        <p>For questions about this Policy or about how we handle your data, email us at <strong>feedback.playrexmortis@gmail.com</strong> or use the feedback form in Settings (category: Other).</p>
      </section>

      <p class="caption" style="margin-top: var(--s-5); text-align: center;">
        Last updated 2026-05-12 · <a href="#/rules">Rules of the Realm</a> · <a href="#/tos">Terms of Service</a>
      </p>
    `;
  });
}
